import * as React from "react";
import { observer } from "mobx-react";
import { WizardHotelPWAv2Props } from "./typings";
import { LobTitle } from "src/components/shared/LobTitle/LobTitle";
import { Location } from "src/components/shared/Location/Location";
import { Dates } from "src/components/shared/Dates/Dates";
import { Travelers } from "src/components/shared/Travelers/Travelers";
import { HotelAddFlightCheckbox } from "components/flexComponents/WizardHotelPWAv2/components/HotelAddFlightCheckbox";
import { HotelAddCarCheckbox } from "components/flexComponents/WizardHotelPWAv2/components/HotelAddCarCheckbox";
import { HotelHiddenSearchData } from "./components/HotelHiddenSearchData";
import {
  UitkLayoutGrid,
  UitkLayoutGridItem,
  UitkLayoutConditionalGridColumnRow,
  UitkLayoutConditionalGridSpan,
} from "uitk-react-layout-grid";
import { NoRehydratationValues, TravelersTriggerTypes } from "src/components/shared/TravelersField/typings";
import { WizardHotelPWAFilter } from "src/components/flexComponents/WizardHotelPWA/components/WizardHotelPWAFilter";
import { MultiplePlaces } from "./components/MultiplePlaces";
import { LobSubtitle } from "src/components/shared/LobSubtitle/LobSubtitle";
import { SubmitRow } from "components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { LobErrorSummary } from "src/components/shared/LobErrorSummary/LobErrorSummary";
import { locKeys } from "components/flexComponents/WizardHotelPWA/components/l10n";
import { UitkText } from "uitk-react-text";
import { useLocalization } from "@shared-ui/localization-context";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { ViewMedium, Viewport, ViewSmall } from "@shared-ui/viewport-context";
import { isVariantEnabled, Experiment } from "src/components/utility/experiment";
import { packagesSubLobs } from "src/stores/wizard/state/typings";

export const WizardHotelPWAv2View: React.FunctionComponent<WizardHotelPWAv2Props> = observer((props) => {
  const { formatText } = useLocalization();
  const {
    hotelWizardState,
    hotelWizardState: { config, numberOfErrors, errorInputRef, moreThanOneError },
  } = props.wizardState;
  const { context } = props;
  const { elementId, enableAddFlight, enableAddCar, enableMultiplePlaces, desktopAutoOpenCalendar } = config;

  const displayTravelersInline = isVariantEnabled(context, Experiment.Alignment_of_Traveler_Input);

  const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 2, large: 12 };
  const locationColSpan: UitkLayoutConditionalGridSpan = { small: 2, large: displayTravelersInline ? 4 : 5 };
  const datesColSpan: UitkLayoutConditionalGridSpan = { small: 2, large: displayTravelersInline ? 8 : 4 };
  const travelersColSpan: UitkLayoutConditionalGridSpan = { small: 2, large: 3 };
  const addFlightColSpan: UitkLayoutConditionalGridSpan = { small: 1, large: 2 };
  const addCarColSpan: UitkLayoutConditionalGridSpan = { small: 1, large: 2 };
  const multiplePlacesColSpan: UitkLayoutConditionalGridSpan = { small: 2, large: 3 };
  const hotelFiltersColSpan: UitkLayoutConditionalGridSpan = { small: 2, large: 8 };

  const isMultiplePlacesDisabled = hotelWizardState.isAddACarChecked || hotelWizardState.isAddAFlightChecked;

  const disclaimerMultiplePlacesLocKey = isMultiplePlacesDisabled
    ? hotelWizardState.config.useDialogTextWithoutCar
      ? locKeys.dialogTextWithoutCar
      : locKeys.dialogText
    : "";

  /**
   * Values used if no rehydratation(JS disabled)
   */
  const noRehydratationValues: NoRehydratationValues = {
    numOfTravelers: 2,
    name: "adults",
  };

  const [autoOpenCalendar, setAutoOpenCalendar] = React.useState<boolean>(Boolean(desktopAutoOpenCalendar));

  const setInputsRefs = (ref: React.RefObject<HTMLInputElement>, secondRef?: React.RefObject<HTMLInputElement>) => {
    hotelWizardState.wizardInputsArray.push(ref);
    if (secondRef) {
      hotelWizardState.wizardInputsArray.push(secondRef);
    }
  };

  const getLocError = (isLink: boolean) => {
    if (!isLink) {
      return hotelWizardState.moreThanOneError()
        ? config.form.submitButton.locMultipleWarningsToken
        : config.form.submitButton.locWarningToken;
    }

    return hotelWizardState.moreThanOneError()
      ? config.form.submitButton.locMultipleWarningsLinkToken
      : config.form.submitButton.locWarningLinkToken;
  };

  /**
   * When user add a flight to the current trip search,
   * a origin field is displayed so as a legend for multiple places (if enabled)
   * Fields below add a flight checkbox should be visible in mobile view
   * Following values are taken from UDS tokens:
   * http://uds.ch.lab.stockyard.io/expedia/tokens/#foundation-breakpoint
   */
  const onAddFlightCheckboxChange = () => {
    const VIEWPORT_MAX_SMALL_BREAKPOINT = 575;
    const SPACE_ABOVE_WIZARD_IN_PIXELS = 200;
    const SPACE_LEFT_WIZARD_IN_PIXELS = 0;
    const SCROLL_BEHAVIOR = "smooth";
    const { innerWidth: viewportSize } = window;

    if (viewportSize < VIEWPORT_MAX_SMALL_BREAKPOINT) {
      window.scrollTo({
        top: SPACE_ABOVE_WIZARD_IN_PIXELS,
        left: SPACE_LEFT_WIZARD_IN_PIXELS,
        behavior: SCROLL_BEHAVIOR,
      });
    }
  };

  const updatePackagesOnReflection = () => {
    const packageState = props.wizardState.packageWizardState;
    const { isAddAFlightChecked, isAddACarChecked } = hotelWizardState;

    if (isAddACarChecked && isAddAFlightChecked) {
      packageState.updateSubLOBState(packagesSubLobs.flightHotelCar);
    } else if (isAddACarChecked) {
      packageState.updateSubLOBState(packagesSubLobs.hotelCar);
    } else if (isAddAFlightChecked) {
      packageState.updateSubLOBState(packagesSubLobs.flightHotel);
    }
  };

  const toggleDatePicker = () => {
    setAutoOpenCalendar(!autoOpenCalendar);
  };

  const getLayoutTravelersInline = (alignment: "end" | "start") => (
    <UitkLayoutFlex
      data-testid="travelers-inline-link"
      alignItems={alignment}
      justifyContent={alignment}
      space="six"
      className="all-l-padding-six"
    >
      <>
        <UitkLayoutFlexItem alignSelf={alignment}>
          <Travelers
            lobState={hotelWizardState}
            colSpan={travelersColSpan}
            noRehydratationValues={noRehydratationValues}
            triggerType={TravelersTriggerTypes.LINK_BUTTON}
          />
        </UitkLayoutFlexItem>
      </>
    </UitkLayoutFlex>
  );

  return (
    <form
      noValidate={true}
      className="WizardHotelPWAv2"
      id={elementId}
      data-fm={props.flexComponent.fmId}
      data-fm-title-id={props.flexComponent.fmTitleId}
      onSubmit={hotelWizardState.submit}
      action={hotelWizardState.formAction}
    >
      {hotelWizardState.config.heading.fixedTitle && (
        <LobTitle lobState={hotelWizardState} customTitle={hotelWizardState.config.heading.title} />
      )}
      <LobSubtitle lobState={hotelWizardState} />

      {displayTravelersInline && (
        <Viewport>
          <ViewSmall>{getLayoutTravelersInline("start")}</ViewSmall>
          <ViewMedium>{getLayoutTravelersInline("end")}</ViewMedium>
        </Viewport>
      )}

      <UitkLayoutGrid columns={mainGridColumns} space="three" className="all-t-margin-three">
        {numberOfErrors > 0 && (
          <LobErrorSummary
            locHeadingToken={getLocError(false)!}
            locHeadingArgs={moreThanOneError() && [numberOfErrors]}
            locLinkToken={getLocError(true)}
            linkClickFocusId="roundtrip-error"
            inputErrorRef={errorInputRef}
            colSpan={mainGridColumns}
            setInputsRefs={setInputsRefs}
          />
        )}
        <Location
          originField={false}
          destinationField={true}
          lobState={hotelWizardState}
          colSpan={locationColSpan}
          setInputsRefs={setInputsRefs}
        />

        <Dates
          lobState={hotelWizardState}
          colSpan={datesColSpan}
          setInputsRefs={setInputsRefs}
          autoOpenCalendar={autoOpenCalendar}
          toggleDatePicker={toggleDatePicker}
        />

        {!displayTravelersInline && (
          <Travelers
            lobState={hotelWizardState}
            colSpan={travelersColSpan}
            noRehydratationValues={noRehydratationValues}
          />
        )}

        {enableMultiplePlaces && isMultiplePlacesDisabled && (
          <UitkLayoutGridItem colSpan={mainGridColumns}>
            <div>
              <UitkText size={300}>{formatText(disclaimerMultiplePlacesLocKey)}</UitkText>
            </div>
          </UitkLayoutGridItem>
        )}

        {enableAddFlight && (
          <UitkLayoutGridItem colSpan={addFlightColSpan}>
            <div>
              <HotelAddFlightCheckbox
                hotelWizardState={hotelWizardState}
                onChange={onAddFlightCheckboxChange}
                updatePackageOnReflection={updatePackagesOnReflection}
              />
            </div>
          </UitkLayoutGridItem>
        )}

        {enableAddCar && (
          <UitkLayoutGridItem colSpan={addCarColSpan}>
            <div>
              <HotelAddCarCheckbox
                hotelWizardState={hotelWizardState}
                updatePackageOnReflection={updatePackagesOnReflection}
              />
            </div>
          </UitkLayoutGridItem>
        )}

        {enableMultiplePlaces && (
          <MultiplePlaces
            hotelWizardState={hotelWizardState}
            disabled={isMultiplePlacesDisabled}
            isChecked={hotelWizardState.isMultiplePlacesChecked}
            colSpan={multiplePlacesColSpan}
          />
        )}
      </UitkLayoutGrid>

      {hotelWizardState.isAddAFlightChecked && (
        <UitkLayoutGrid columns={mainGridColumns}>
          <UitkLayoutGridItem colSpan={locationColSpan}>
            <div>
              <Location originField={true} destinationField={false} lobState={hotelWizardState} />
            </div>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      )}

      {hotelWizardState.config.includeFiltersHotelPWAWizard && (
        <UitkLayoutGrid columns={mainGridColumns}>
          <UitkLayoutGridItem colSpan={hotelFiltersColSpan}>
            <div>
              <WizardHotelPWAFilter
                changeFormProperties={hotelWizardState.changeFormFilterProperties}
                checkboxFilterChecks={hotelWizardState.checkboxFilterChecks}
                advancedOptionsFilterVisible={hotelWizardState.advancedOptionsFilterVisible}
              />
            </div>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      )}

      <SubmitRow lobState={hotelWizardState} rfrr={"SearchClick_Hotel"} />

      <HotelHiddenSearchData hotelWizardState={hotelWizardState} />
    </form>
  );
});

export default WizardHotelPWAv2View;
